import { captureElement } from "@utils/capture_utils";

export interface Rect { x: number; y: number; w: number; h: number; }

// Nodes carrying this attribute are excluded from the capture (our own button/overlay/rect).
export const SCREENGRAB_UI_ATTR = "data-screengrab-ui";

const MAX_BYTES = 5 * 1024 * 1024; // matches the composer's per-image cap

const canvasToPngBlob = (canvas: HTMLCanvasElement): Promise<Blob> =>
  new Promise((resolve, reject) =>
    canvas.toBlob(
      b => (b ? resolve(b) : reject(new Error("toBlob returned null"))),
      "image/png",
    ),
  );

function downscale(src: HTMLCanvasElement, factor: number): HTMLCanvasElement {
  const out = document.createElement("canvas");
  out.width = Math.max(1, Math.round(src.width * factor));
  out.height = Math.max(1, Math.round(src.height * factor));
  out.getContext("2d")!.drawImage(src, 0, 0, out.width, out.height);
  return out;
}

// html2canvas clones the DOM synchronously on the main thread, so without an explicit yield
// it starts before the browser paints the caller's just-set loading state (the "Capturing…"
// indicator would never appear). Double rAF guarantees exactly one paint first.
const nextPaint = (): Promise<void> =>
  new Promise(resolve =>
    requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
  );

const captureOptions = () => ({
  ignoreElements: (el: Element) =>
    (el instanceof HTMLElement && el.hasAttribute(SCREENGRAB_UI_ATTR)) ||
    // Novu's inbox (shadow DOM) styles itself with oklch() colors, which html2canvas 1.x
    // can't parse — it throws and kills the whole capture. Skip the subtree; the navbar
    // bell is just omitted from screenshots.
    el.classList.contains("novu-inbox-wrapper"),
  logging: false,
  // Cross-origin logo handling (CORS re-fetch, cache-bust, no-CORS retry) lives in captureElement.
  onclone: (doc: Document) => {
    // react-grid-layout positions tiles with `transform: translate()` on an `overflow: hidden`
    // box. html2canvas 1.x computes the overflow clip before applying the transform, so tile
    // contents get clipped by a rect shifted up/left by the translate amount, so tiles below
    // the first row come out as blank white cards. Bake the translate into left/top instead.
    doc.querySelectorAll<HTMLElement>(".react-grid-item").forEach(el => {
      const m = doc.defaultView!.getComputedStyle(el).transform.match(/matrix\(([^)]+)\)/);
      if (!m) return;
      const [tx, ty] = m[1].split(",").map(Number).slice(4);
      el.style.transform = "none";
      el.style.left = `${tx}px`;
      el.style.top = `${ty}px`;
    });
    // The sandbox gutters are sized background layers; html2canvas ignores background-size
    // and paints the gradient across the full width. Plain colour in the clone.
    doc.querySelectorAll<HTMLElement>(".dashboard-grid-scroll-wrapper").forEach(el => {
      el.style.background = "#f5f5f5";
    });
  },
  // Render only the visible viewport, not the full scrollable page. Bounds the output
  // canvas to viewport size, avoiding the browser's max-canvas-size limit on tall pages
  // (which would silently return a blank/truncated canvas).
  x: window.scrollX,
  y: window.scrollY,
  width: window.innerWidth,
  height: window.innerHeight,
});

// Capture the given viewport rect as a PNG blob. Renders ONLY the visible viewport with
// html2canvas (skipping our own UI), crops to the rect, then downscales if the PNG exceeds
// the composer's 5 MB cap.
export async function captureRegion(rect: Rect): Promise<Blob> {
  await nextPaint();
  const source = await captureElement(document.documentElement, captureOptions());

  // The output canvas maps 1:1 to the viewport (its top-left = the viewport's top-left), so
  // the selection rect (already in client/viewport coords) needs no scroll offset — only the
  // device-pixel scale html2canvas rendered at.
  const scale = source.width / window.innerWidth;
  const sx = rect.x * scale;
  const sy = rect.y * scale;
  const sw = rect.w * scale;
  const sh = rect.h * scale;

  let cropped = document.createElement("canvas");
  cropped.width = Math.max(1, Math.round(sw));
  cropped.height = Math.max(1, Math.round(sh));
  cropped
    .getContext("2d")!
    .drawImage(source, sx, sy, sw, sh, 0, 0, cropped.width, cropped.height);

  let blob = await canvasToPngBlob(cropped);
  // Downscale until under the cap (rare — only very large grabs). Iteration cap is a backstop.
  for (let i = 0; i < 6 && blob.size > MAX_BYTES; i++) {
    cropped = downscale(cropped, 0.7);
    blob = await canvasToPngBlob(cropped);
  }
  // Never return an oversized blob: the composer would reject it and we'd land on the chatbot
  // with the prompt but no image. Fail loudly instead — the caller shows an error and does not
  // navigate.
  if (blob.size > MAX_BYTES) throw new Error("Screenshot too large after downscaling");
  return blob;
}
